import React, { useEffect, useMemo, useState } from "react";
import { currencySymbol, formatAmountForCopy, isSuffixCurrency } from "../utils/currency";
import { maxCounterFor, minRequestPriceFor, type TierTalent } from "../utils/payinConfig";
import NumberPad from "./NumberPad";
import "./DeclineModal.css";
import { t } from "../i18n";

type Reason =
  | "pas_ambience"
  | "no_access"
  | "already_played"
  | "more_money"
  | "not_sharing_setlist";

// Server cap (routes/orders.js + payments.js) is per-currency now (Phase C);
// mirror it client-side so the modal blocks the same values the server would.

interface Props {
  isOpen: boolean;
  /** Talent's currency — drives every symbol in the modal (never "$"-hardcoded). */
  currency?: string | null;
  /** Talent's cap tier (fraud door): admin-lifted accounts get the
      roomier counter ceiling; absent/false = the tight default. */
  capsLifted?: boolean | null;
  /** The talent's own profile (minRequestPrice from the server) — counters can't go under it. */
  talent?: TierTalent;
  /** Setlist sale — song reasons make no sense; the founder's one decline
      reason + the counter row instead (founder, 2026-08-21). */
  isPlaylist?: boolean;
  /** The practice card (founder, build 44): the same sheet, marked PRACTICE.
      Nothing is sent: the page finishes the card on any answer. */
  practice?: boolean;
  onClose: () => void;
  onConfirm: (reason: Reason, counterAmount?: number) => void;
  /** The talent can't take money yet (no bank): "Request a higher amount"
      asks a fan to pay, so it opens Connect Bank instead of the counter pad
      (founder, 1.5.2 b49). The server refuses such a counter too. */
  onNeedBank?: () => void;
}

const REASONS: Array<{ key: Reason; emoji: string; label: string }> = [
  { key: "pas_ambience", emoji: "🎧", label: t("declineModal.notTheVibe") },
  { key: "no_access", emoji: "🚫", label: t("declineModal.iDonTHave") },
  // Third slot on purpose: keeps the two long-standing options in their
  // muscle-memory positions and the gold counter row last.
  { key: "already_played", emoji: "🔁", label: t("declineModal.alreadyPlayedIt") },
  { key: "more_money", emoji: "💰", label: t("declineModal.requestAHigherAmount") },
];

// Playlist sales: the fan sees this exact sentence as the reason.
const PLAYLIST_REASONS: Array<{ key: Reason; emoji: string; label: string }> = [
  { key: "not_sharing_setlist", emoji: "🙅", label: t("declineModal.sorryIAmNot") },
  { key: "more_money", emoji: "💰", label: t("declineModal.requestAHigherAmount") },
];

// Redesign (founder pick "Option 1"): tappable reason cards, and
// the counter amount is entered on the app's own NumberPad against a
// read-only display — no <input>, so the iOS focus auto-zoom (triggered by
// sub-16px input fonts) can't happen. Confirm turns green for the counter
// path: that's a money action, not a decline.
const DeclineModal: React.FC<Props> = ({ isOpen, currency, capsLifted = false, talent, isPlaylist = false, practice = false, onClose, onConfirm, onNeedBank }) => {
  const defaultReason: Reason = isPlaylist ? "not_sharing_setlist" : "pas_ambience";
  const [reason, setReason] = useState<Reason>(defaultReason);
  const [amount, setAmount] = useState("");
  const [padOpen, setPadOpen] = useState(false);

  const sym = currencySymbol(currency);
  // "0 Ft", not "Ft0" — the eight suffix currencies write the symbol last.
  const suffixCur = isSuffixCurrency(currency);

  const maxCounter = maxCounterFor(currency, capsLifted === true);
  // The server refuses a counter under the talent's minimum request price
  // ($5 class since 2026-10-30, or their own line) — say so here, the same
  // way as the max.
  const minCounter = minRequestPriceFor(currency, talent);
  const isMoney = reason === "more_money";
  const reasons = isPlaylist ? PLAYLIST_REASONS : REASONS;

  // Fresh state on every open.
  useEffect(() => {
    if (!isOpen) return;
    setReason(defaultReason);
    setAmount("");
    setPadOpen(false);
  }, [isOpen, defaultReason]);

  const parsed = useMemo(() => {
    if (amount.trim() === "") return null;
    const n = Number(amount);
    return Number.isFinite(n) ? n : NaN;
  }, [amount]);

  const amountError = useMemo<string | null>(() => {
    if (!isMoney || parsed === null) return null;
    if (Number.isNaN(parsed) || parsed <= 0) return t("declineModal.enterAValidAmount");
    if (parsed > maxCounter) {
      return t("declineModal.maxCounterIs", { amount: formatAmountForCopy(maxCounter, currency) });
    }
    // Only once the pad is closed: typing "15" passes through "1" on the way.
    if (parsed < minCounter && !padOpen) {
      return t("declineModal.minCounterIs", { amount: formatAmountForCopy(minCounter, currency) });
    }
    return null;
  }, [isMoney, parsed, maxCounter, minCounter, currency, padOpen]);

  const amountValid =
    parsed !== null &&
    !Number.isNaN(parsed) &&
    parsed >= minCounter &&
    parsed <= maxCounter;

  if (!isOpen) return null;

  const handlePad = (next: string) => {
    // Money: two decimal places max.
    const dot = next.indexOf(".");
    if (dot !== -1 && next.length - dot - 1 > 2) return;
    setAmount(next);
  };

  const pickReason = (r: Reason) => {
    if (r === "more_money" && onNeedBank) {
      onNeedBank();
      return;
    }
    setReason(r);
    setPadOpen(r === "more_money");
  };

  const handleConfirm = () => {
    if (isMoney && !amountValid) return;
    onConfirm(reason, isMoney && parsed !== null ? parsed : undefined);
  };

  return (
    <>
      <div className="decline-modal-overlay" onClick={onClose}>
        <div
          className={`decline-modal ${padOpen ? "decline-modal--pad" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {practice && <span className="decline-practice-chip">{t("declineModal.practice")}</span>}
          <h3 style={{ marginTop: 0, marginBottom: 4 }}>{t("declineModal.whyAreYouDeclining")}</h3>
          <p className={practice ? "decline-sub decline-sub--practice" : "decline-sub"}>
            {practice ? t("declineModal.practiceFansSeeReply") : t("declineModal.theFanSeesYour")}
          </p>

          {reasons.map((r) => (
            <button
              key={r.key}
              type="button"
              className={`decline-opt ${reason === r.key ? "decline-opt--sel" : ""}`}
              onClick={() => pickReason(r.key)}
            >
              <span className="decline-opt-emoji" aria-hidden="true">{r.emoji}</span>
              {r.label}
              <span className="decline-opt-check" aria-hidden="true">✓</span>
            </button>
          ))}

          {isMoney && (
            <div
              className="decline-amt-block"
              role="textbox"
              aria-readonly="true"
              aria-label={t("declineModal.newAmount")}
              onClick={() => setPadOpen(true)}
            >
              <div className="decline-amt-label">{t("declineModal.newAmount")}</div>
              <div className="decline-amt-display">
                {!suffixCur && sym}
                {amount === "" && !padOpen ? (
                  <span className="decline-amt-placeholder">0</span>
                ) : (
                  amount
                )}
                {suffixCur && ` ${sym}`}
                {padOpen && <span className="decline-amt-cursor" aria-hidden="true" />}
              </div>
              {amountError ? (
                <div className="decline-amt-error" role="alert">{amountError}</div>
              ) : (
                <div className="decline-amt-hint">
                  {t("declineModal.fanCanAcceptAnd")}
                </div>
              )}
            </div>
          )}

          <div className="decline-actions">
            <button className="decline-cancel" onClick={onClose}>
              {t("declineModal.cancel")}
            </button>
            <button
              className={`decline-confirm ${isMoney ? "decline-confirm--money" : ""}`}
              onClick={handleConfirm}
              disabled={isMoney && !amountValid}
            >
              {isMoney
                ? amountValid && parsed !== null
                  ? t("declineModal.sendCounter", { amount: formatAmountForCopy(parsed, currency) })
                  : t("declineModal.sendCounter2")
                : t("declineModal.declineRequest")}
            </button>
          </div>
        </div>
      </div>

      {/* Portaled to <body>; kept OUTSIDE the overlay so pad taps don't
          bubble (through the React tree) into the overlay's close handler. */}
      <NumberPad
        open={padOpen}
        value={amount}
        onChange={handlePad}
        onDone={() => setPadOpen(false)}
        allowDecimal
        maxLength={8}
      />
    </>
  );
};

export default DeclineModal;
